import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  SelectField,
  SearchInput,
  SelectInput,
  FunctionField,
  useRecordContext,
  EditButton,
  DeleteButton,
  ShowButton,
  CreateButton,
  TopToolbar,
  DeleteWithConfirmButton,
} from 'react-admin';
import {
  TicketStatus,
  TicketPriority,
  TicketCategory,
} from '../../generated/data-contracts';
import { Card, Typography, Box, Chip } from '@mui/material';
import {
  Add as AddIcon,
  ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import CustomCreateButton from '@/components/CustomCreateButton';

const TicketListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Ticket'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

// Filtros para la búsqueda
const ticketFilters = [
  <SearchInput
    key='search'
    source='SearchText'
    placeholder='Buscar tickets...'
    alwaysOn
  />,
  <SelectInput
    key='status'
    source='Status'
    label='Estado'
    choices={[
      { id: TicketStatus.Open, name: 'Abierto' },
      { id: TicketStatus.InProgress, name: 'En Progreso' },
      { id: TicketStatus.Resolved, name: 'Resuelto' },
      { id: TicketStatus.Closed, name: 'Cerrado' },
      { id: TicketStatus.Cancelled, name: 'Cancelado' },
    ]}
    emptyText='Todos los estados'
  />,
  <SelectInput
    key='priority'
    source='Priority'
    label='Prioridad'
    choices={[
      { id: TicketPriority.Low, name: 'Baja' },
      { id: TicketPriority.Medium, name: 'Media' },
      { id: TicketPriority.High, name: 'Alta' },
      { id: TicketPriority.Critical, name: 'Crítica' },
    ]}
    emptyText='Todas las prioridades'
  />,
  <SelectInput
    key='category'
    source='Category'
    label='Categoría'
    choices={[
      { id: TicketCategory.Bug, name: 'Error' },
      { id: TicketCategory.Feature, name: 'Funcionalidad' },
      { id: TicketCategory.Support, name: 'Soporte' },
      { id: TicketCategory.Contact, name: 'Contacto' },
      { id: TicketCategory.General, name: 'General' },
    ]}
    emptyText='Todas las categorías'
  />,
];

// Componente para renderizar el estado con colores
const StatusField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusColors = {
    [TicketStatus.Open]: 'primary',
    [TicketStatus.InProgress]: 'secondary',
    [TicketStatus.Resolved]: 'success',
    [TicketStatus.Closed]: 'default',
    [TicketStatus.Cancelled]: 'error',
  } as const;

  const statusLabels = {
    [TicketStatus.Open]: 'Abierto',
    [TicketStatus.InProgress]: 'En Progreso',
    [TicketStatus.Resolved]: 'Resuelto',
    [TicketStatus.Closed]: 'Cerrado',
    [TicketStatus.Cancelled]: 'Cancelado',
  };

  return (
    <Chip
      label={statusLabels[record.status as TicketStatus] || record.status}
      color={statusColors[record.status as TicketStatus] || 'default'}
      size='small'
    />
  );
};

// Componente para renderizar la prioridad con colores
const PriorityField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const priorityColors = {
    [TicketPriority.Low]: 'default',
    [TicketPriority.Medium]: 'primary',
    [TicketPriority.High]: 'warning',
    [TicketPriority.Critical]: 'error',
  } as const;

  const priorityLabels = {
    [TicketPriority.Low]: 'Baja',
    [TicketPriority.Medium]: 'Media',
    [TicketPriority.High]: 'Alta',
    [TicketPriority.Critical]: 'Crítica',
  };

  return (
    <Chip
      label={
        priorityLabels[record.priority as TicketPriority] || record.priority
      }
      color={priorityColors[record.priority as TicketPriority] || 'default'}
      size='small'
    />
  );
};

export const TicketsList: React.FC = () => (
  <Card elevation={1} sx={{ m: 2, p: 2 }}>
    <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography
        variant='h5'
        component='h1'
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <TicketIcon color='primary' />
        Gestión de Tickets
      </Typography>
    </Box>
    <List
      title={false}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      filters={ticketFilters}
      resource='tickets-admin'
      actions={<TicketListActions />}
      sx={{ '& .RaList-main': { mt: 0 } }}
    >
      <Datagrid
        rowClick='show'
        sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}
      >
        <TextField source='id' label='ID' />
        <TextField source='title' label='Título' />
        <FunctionField label='Estado' render={() => <StatusField />} />
        <FunctionField label='Prioridad' render={() => <PriorityField />} />
        <SelectField
          source='category'
          label='Categoría'
          choices={[
            { id: TicketCategory.Bug, name: 'Error' },
            { id: TicketCategory.Feature, name: 'Funcionalidad' },
            { id: TicketCategory.Support, name: 'Soporte' },
            { id: TicketCategory.Contact, name: 'Contacto' },
            { id: TicketCategory.General, name: 'General' },
          ]}
        />
        <TextField source='createdByUserName' label='Creado por' />
        <TextField source='assignedToUserName' label='Asignado a' />
        <DateField source='createdAt' label='Fecha de creación' showTime />
        <DateField source='updatedAt' label='Última actualización' showTime />
        <ShowButton label={false}/>
        <EditButton label={false}/>
        <DeleteWithConfirmButton
          confirmTitle='Confirmar eliminación'
          confirmContent={`¿Seguro que quieres eliminar el ticket ? Esta acción no se puede deshacer.`}
          mutationMode='pessimistic'
          label={false}
        />
      </Datagrid>
    </List>
  </Card>
);
